import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import timeTrackingService from '../../services/timeTrackingService';

const TimeEntryForm = ({ entry, projects, tasks, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    project_id: '',
    task_id: '',
    description: '',
    entry_date: new Date().toISOString().split('T')[0],
    start_time: '',
    end_time: '',
    hours_spent: '',
    is_billable: true,
    hourly_rate: '',
    status: 'draft'
  });

  const [useTimeRange, setUseTimeRange] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (entry) {
      setFormData({
        project_id: entry.project_id || '',
        task_id: entry.task_id || '',
        description: entry.description || '',
        entry_date: entry.entry_date || new Date().toISOString().split('T')[0],
        start_time: entry.start_time ? new Date(entry.start_time).toISOString().slice(0, 16) : '',
        end_time: entry.end_time ? new Date(entry.end_time).toISOString().slice(0, 16) : '',
        hours_spent: entry.hours_spent || '',
        is_billable: entry.is_billable !== undefined ? entry.is_billable : true,
        hourly_rate: entry.hourly_rate || '',
        status: entry.status || 'draft'
      });
      setUseTimeRange(!!(entry.start_time && entry.end_time));
    }
  }, [entry]);

  // Calculate hours when time range changes
  useEffect(() => {
    if (useTimeRange && formData.start_time && formData.end_time) {
      const hours = timeTrackingService.calculateDuration(formData.start_time, formData.end_time);
      setFormData(prev => ({ ...prev, hours_spent: hours }));
    }
  }, [formData.start_time, formData.end_time, useTimeRange]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.project_id) {
      newErrors.project_id = 'Project is required';
    }

    if (!formData.entry_date) {
      newErrors.entry_date = 'Date is required';
    }

    if (useTimeRange) {
      if (!formData.start_time) {
        newErrors.start_time = 'Start time is required';
      }
      if (!formData.end_time) {
        newErrors.end_time = 'End time is required';
      }
      if (formData.start_time && formData.end_time && formData.start_time >= formData.end_time) {
        newErrors.end_time = 'End time must be after start time';
      }
    } else {
      if (!formData.hours_spent || formData.hours_spent <= 0) {
        newErrors.hours_spent = 'Hours must be greater than 0';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    try {
      const submitData = {
        ...formData,
        task_id: formData.task_id || null,
        hourly_rate: formData.hourly_rate ? parseFloat(formData.hourly_rate) : null
      };

      if (!useTimeRange) {
        delete submitData.start_time;
        delete submitData.end_time;
      }

      await onSubmit(submitData);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save time entry');
    }
  };

  const filteredTasks = tasks?.filter(task => 
    !formData.project_id || task.project_id === formData.project_id
  ) || [];

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-2 gap-6">
        {/* Project Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Project *
          </label>
          <select
            name="project_id"
            value={formData.project_id}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              errors.project_id ? 'border-red-500' : 'border-gray-300'
            }`}
            required
          >
            <option value="">Select a project</option>
            {projects?.map(project => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>
          {errors.project_id && (
            <p className="mt-1 text-sm text-red-600">{errors.project_id}</p>
          )}
        </div>

        {/* Task Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Task (Optional)
          </label>
          <select
            name="task_id"
            value={formData.task_id}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={!formData.project_id}
          >
            <option value="">No task selected</option>
            {filteredTasks.map(task => (
              <option key={task.id} value={task.id}>
                {task.title}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Description
        </label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleChange}
          rows="3"
          placeholder="What did you work on?"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>
       <div className="grid grid-cols-2 gap-6">
        
      {/* Date */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Date *
        </label>
        <input
          type="date"
          name="entry_date"
          value={formData.entry_date}
          onChange={handleChange}
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
            errors.entry_date ? 'border-red-500' : 'border-gray-300'
          }`}
          required
        />
        {errors.entry_date && (
          <p className="mt-1 text-sm text-red-600">{errors.entry_date}</p>
        )}

      </div>

            {/* Status */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Status
        </label>
        <select
          name="status"
          value={formData.status}
          onChange={handleChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="draft">Draft</option>
          <option value="submitted">Submitted</option>
          <option value="approved">Approved</option>
        </select>
      </div>
       </div>

      {/* Time Entry Method Toggle */}
  <div className="grid grid-cols-4 gap-6">
  <label className="flex items-center">
    <input
      type="radio"
      checked={!useTimeRange}
      onChange={() => setUseTimeRange(false)}
      className="mr-2"
    />
    <span className="text-sm font-medium text-gray-700">Manual Hours</span>
  </label>
  <label className="flex items-center">
    <input
      type="radio"
      checked={useTimeRange}
      onChange={() => setUseTimeRange(true)}
      className="mr-2"
    />
    <span className="text-sm font-medium text-gray-700">Time Range</span>
  </label>

  <div className="flex items-center gap-2">
    <input
      type="checkbox"
      name="is_billable"
      checked={formData.is_billable}
      onChange={handleChange}
      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
    />
    <label className="text-sm font-medium text-gray-700">Billable</label>
  </div>

  {formData.is_billable && (
    <div className="flex items-center gap-2">
      <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
        Hourly Rate
      </label>
      <input
        type="number"
        name="hourly_rate"
        value={formData.hourly_rate}
        onChange={handleChange}
        step="0.01"
        min="0"
        placeholder="e.g., 50.00"
        className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      />
    </div>
  )}
</div>

      {/* Time Input */}
      {useTimeRange ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Time *
            </label>
            <input
              type="datetime-local"
              name="start_time"
              value={formData.start_time}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.start_time ? 'border-red-500' : 'border-gray-300'
              }`}
              required
            />
            {errors.start_time && (
              <p className="mt-1 text-sm text-red-600">{errors.start_time}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Time *
            </label>
            <input
              type="datetime-local"
              name="end_time"
              value={formData.end_time}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.end_time ? 'border-red-500' : 'border-gray-300'
              }`}
              required
            />
            {errors.end_time && (
              <p className="mt-1 text-sm text-red-600">{errors.end_time}</p>
            )}
          </div>
        </div>
      ) : (
        <div className='w-1/2'>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Hours Spent *
          </label>
          <input
            type="number"
            name="hours_spent"
            value={formData.hours_spent}
            onChange={handleChange}
            step="0.25"
            min="0"
            placeholder="e.g., 2.5"
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              errors.hours_spent ? 'border-red-500' : 'border-gray-300'
            }`}
            required
          />
          {errors.hours_spent && (
            <p className="mt-1 text-sm text-red-600">{errors.hours_spent}</p>
          )}
        </div>
      )}

      {/* Calculated Hours Display */}
      {useTimeRange && formData.hours_spent > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <p className="text-sm text-blue-800">
            <strong>Calculated Duration:</strong> {timeTrackingService.formatDuration(formData.hours_spent)}
          </p>
        </div>
      )}

      {/* Calculated Amount */}
      {formData.is_billable && formData.hourly_rate && formData.hours_spent && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
          <p className="text-sm text-green-800">
            <strong>Billable Amount:</strong> $
            {(parseFloat(formData.hours_spent) * parseFloat(formData.hourly_rate)).toFixed(2)}
          </p>
        </div>
      )}


      {/* Form Actions */}
      <div className="flex justify-end space-x-3 pt-4 border-t">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
        >
          {entry ? 'Update' : 'Create'} Time Entry
        </button>
      </div>
    </form>
  );
};

export default TimeEntryForm;

